import type { PublicUser } from '../models/PublicUser'
import type { RoomStateEngagement } from '../models/RoomStateEngagement'
import type { RoomState, RoomStateStatus } from '../models/RoomState'
import type { SlideRubric } from '../models/SlideRubric'
import type { RoomStateRubricResult } from '../models/RoomStateRubricResult'
import type { RoomStateSummary } from '../models/RoomStateSummary'
import type { SlideQuestion } from '../models/SlideQuestion'
import type { PromptRubricType } from '../models/PromptRubricType'
import type { SectionAssignment } from '../models/SectionAssignment'
import type { FirestoreAssignmentRubric } from '../firestore/AssignmentRubric'
import {
  calculateQuizScoreForUser,
  calculateTotalRubricScoreForUserFloat,
  calculateEngagementScoreForUser,
  calculateAttendanceScoreForUser,
  calculateScalarRawScoreDataForUser,
  calculateMedianEngagementDuration,
} from '../sources-of-truth/scoring'

// Extracted from InstructorAssignmentCubit so it is initialized before the
// cubit's class body evaluates. When emitDecoratorMetadata is on, the cubit's
// `@computed get emptyGroupWithAllUsersNotInGroup(): AssignmentGroupData`
// emits a runtime reference to this class in the __decorate() call that runs
// when the cubit class closes; keeping the definition in the same file, below
// the cubit, put it in the temporal dead zone and threw a ReferenceError at
// load time (the reason the server used to load the cubit from the CJS dist).
export class AssignmentGroupData {
  public absentUsers: PublicUser[]
  public engagementData: Map<string, RoomStateEngagement>
  public groupLeader?: string
  public groupName?: string // this should render as `not in a group` if undefined
  public groupMembers: string[]
  public groupSortingAnswerScores: Map<string, number>
  public roomStateStatus: RoomStateStatus
  public roomState?: RoomState
  public quizScore: Map<string, number>
  public quizScoreByUserIdAndSlideId: Map<string, Map<string, number>>
  public rubricResults: Map<string, Map<SlideRubric, RoomStateRubricResult[]>>
  public summary?: RoomStateSummary
  public sortingQuizScores: Map<string, Map<string, number>>
  public sortingQuestions: Map<string, SlideQuestion>
  public users: PublicUser[]
  public usersHaveTakenQuiz: Set<string>
  public rubricTypes?: PromptRubricType[] = []
  public assignmentRubrics?: Map<string, FirestoreAssignmentRubric>

  constructor({
    absentUsers = [],
    groupName,
    groupMembers,
    roomState,
    summary,
    engagementData = new Map(),
    groupLeader,
    groupSortingAnswerScores = new Map(),
    quizScore = new Map(),
    quizScoreByUserIdAndSlideId = new Map(),
    sortingQuizScores = new Map(),
    sortingQuestions = new Map(),
    roomStateStatus,
    rubricResults = new Map(),
    users = [],
    usersHaveTakenQuiz = new Set<string>(),
    promptRubricTypes,
    assignmentRubrics,
  }: {
    absentUsers?: PublicUser[]
    groupName: string
    groupMembers: string[]
    roomState: RoomState
    summary: RoomStateSummary
    engagementData?: Map<string, RoomStateEngagement>
    groupLeader?: string
    groupSortingAnswerScores?: Map<string, number>
    quizScore?: Map<string, number>
    quizScoreByUserIdAndSlideId?: Map<string, Map<string, number>>
    sortingQuizScores?: Map<string, Map<string, number>>
    sortingQuestions?: Map<string, SlideQuestion>
    roomStateStatus: RoomStateStatus
    rubricResults?: Map<string, Map<SlideRubric, RoomStateRubricResult[]>>
    users: PublicUser[]
    usersHaveTakenQuiz: Set<string>
    promptRubricTypes?: PromptRubricType[]
    assignmentRubrics?: Map<string, FirestoreAssignmentRubric>
  }) {
    this.absentUsers = absentUsers
    this.groupName = groupName
    this.groupMembers = groupMembers
    this.roomState = roomState
    this.summary = summary
    this.engagementData = engagementData
    this.groupLeader = groupLeader
    this.groupSortingAnswerScores = groupSortingAnswerScores
    this.quizScore = quizScore
    this.quizScoreByUserIdAndSlideId = quizScoreByUserIdAndSlideId
    this.sortingQuizScores = sortingQuizScores
    this.sortingQuestions = sortingQuestions
    this.roomStateStatus = roomStateStatus
    this.rubricResults = rubricResults
    this.users = users
    this.usersHaveTakenQuiz = usersHaveTakenQuiz
    this.rubricTypes = promptRubricTypes
    this.assignmentRubrics = assignmentRubrics
  }

  getResultsByUserIdForRubric(rubric: SlideRubric) {
    const resultsPerUser = new Map<string, RoomStateRubricResult>()
    for (const [userId, rubricResults] of this.rubricResults.entries()) {
      const rubricResult = rubricResults.get(rubric)
      if (rubricResult && rubricResult.length > 0) {
        resultsPerUser.set(userId, rubricResult[0])
      }
    }
    return resultsPerUser
  }

  // `totalRubricScore` internal scalar (on assignment)
  // `Critical Thinking` for copy/UI
  totalRubricScoreForUserFloat(userId: string) {
    return calculateTotalRubricScoreForUserFloat({
      userId,
      rubricResults: this.rubricResults,
      rubricTypes: this.rubricTypes,
      roomState: this.roomState,
      assignmentRubrics: this.assignmentRubrics,
    })
  }

  // `attendanceScore` internal scalar (on assignment)
  // `Attendance` for copy/UI
  attendanceScoreForUser(userId: string) {
    return calculateAttendanceScoreForUser({
      userId,
      engagementData: this.engagementData,
    })
  }

  // `participationScore` internal scalar (on assignment)
  // `Completion` for copy/UI
  engagementScoreForUser(userId: string) {
    return calculateEngagementScoreForUser({
      userId,
      engagementData: this.engagementData,
      medianEngagementDuration: this.medianEngagementDuration,
    })
  }

  quizScoreForUser(userId: string) {
    return calculateQuizScoreForUser({
      userId,
      quizScores: this.quizScore,
    })
  }

  scalarRawScoreDataForUser(
    userId: string,
    scalars: SectionAssignment['data']['assignmentGradingScalars'],
    { nullIfNoScalars }: { nullIfNoScalars: boolean } = {
      nullIfNoScalars: true,
    }
  ) {
    return calculateScalarRawScoreDataForUser({
      userId,
      scalars,
      quizScores: this.quizScore,
      rubricResults: this.rubricResults,
      rubricTypes: this.rubricTypes,
      roomState: this.roomState,
      engagementData: this.engagementData,
      medianEngagementDuration: this.medianEngagementDuration,
      assignmentRubrics: this.assignmentRubrics,
      nullIfNoScalars,
    })
  }

  scalarScoreDataForUser(
    userId: string,
    scalars: SectionAssignment['data']['assignmentGradingScalars'],
    options: { nullIfNoScalars: boolean } = { nullIfNoScalars: true }
  ) {
    const scores = this.scalarRawScoreDataForUser(userId, scalars, options)

    if (!scores) return null

    const scoresAsStrings: {
      participationScore: string
      quizScore?: string | null
      totalRubricScore?: string
      totalScore: string
      attendanceScore: string
    } = {
      participationScore: '',
      attendanceScore: '',
      totalScore: '',
    }

    const keys: Array<keyof typeof scores> = [
      'quizScore',
      'totalRubricScore',
      'participationScore',
      'attendanceScore',
      'totalScore',
    ]

    for (const key of keys) {
      if (!(key in scores)) continue
      const value = scores[key]
      // Handle null quiz score (no MC questions in slide deck)
      if (value === null) {
        scoresAsStrings[key as 'quizScore'] = null
        continue
      }
      scoresAsStrings[key] = Math.ceil(value! * 100) + '%'
    }

    return scoresAsStrings
  }

  get medianEngagementDuration() {
    return calculateMedianEngagementDuration(
      Array.from(this.engagementData.values())
    )
  }
}
